import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Paper,
  Loader,
  Center,
  Table,
  Pagination,
  Button,
  TextInput,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconPackage,
  IconDownload,
  IconFilterOff,
  IconAlertCircle,
  IconSearch,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { MaterialHistoryKind, MaterialHistorySourceType } from '../../lib/api/types/lager';

const PAGE_SIZE = 25;

const KIND_LABEL: Record<MaterialHistoryKind, string> = {
  DELIVERY: 'Lieferung',
  RETURN: 'Rückgabe',
};

const KIND_COLOR: Record<MaterialHistoryKind, string> = {
  DELIVERY: 'teal',
  RETURN: 'orange',
};

const SOURCE_LABEL: Record<MaterialHistorySourceType, string> = {
  submission: 'Liefernachweis',
  movement: 'Bestandsbuchung',
  return: 'Rückgabe',
  delivery_order: 'Auslieferungsauftrag',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  customerId: string;
  active: boolean;
}

export function MaterialHistoryTab({ customerId, active }: Props) {
  const [page, setPage] = useState(1);
  const [articleQuery, setArticleQuery] = useState('');
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null]);

  const hasFilter = !!(articleQuery || dateRange[0] || dateRange[1]);

  const filterParams = {
    articleQuery: articleQuery || undefined,
    from: dateRange[0] || undefined,
    to: dateRange[1] || undefined,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['customer-material-history', customerId, page, articleQuery, dateRange[0], dateRange[1]],
    queryFn: () =>
      api.lagerGetCustomerMaterialHistory(customerId, {
        ...filterParams,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: active && !!customerId,
    staleTime: 60_000,
  });

  const rows = data?.data ?? [];
  const total = data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetFilters = () => {
    setArticleQuery('');
    setDateRange([null, null]);
    setPage(1);
  };

  const exportCsv = async () => {
    const res = await api.lagerGetCustomerMaterialHistory(customerId, {
      ...filterParams,
      page: 1,
      pageSize: 5000,
    });
    const header = ['Datum', 'Techniker', 'Vorgang', 'Artikel', 'Artikel-Nr.', 'Menge', 'SNR', 'Beleg', 'Quelle'];
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const csvRows = res.data.map((r) => [
      formatDate(r.date),
      r.technicianName,
      KIND_LABEL[r.kind],
      r.articleName,
      r.articleNumber ?? '',
      String(r.quantity),
      r.serial ?? '',
      r.ticketRef ?? '',
      SOURCE_LABEL[r.sourceType],
    ]);
    const csv = [header, ...csvRows].map((row) => row.map(escape).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'material-historie.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Stack gap="md">
      {/* Filter-Zeile */}
      <Paper withBorder p="sm">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput
            label="Artikel-Suche"
            placeholder="Name oder Artikelnr."
            leftSection={<IconSearch size={14} />}
            value={articleQuery}
            onChange={(e) => { setArticleQuery(e.currentTarget.value); setPage(1); }}
            w={200}
          />
          <DatePickerInput
            type="range"
            label="Zeitraum"
            placeholder="Von – Bis"
            value={dateRange}
            onChange={(v) => { setDateRange(v as [string | null, string | null]); setPage(1); }}
            clearable
            w={220}
          />
          {hasFilter && (
            <Tooltip label="Filter zurücksetzen">
              <ActionIcon variant="subtle" color="gray" onClick={resetFilters} mt={20}>
                <IconFilterOff size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconDownload size={14} />}
              onClick={exportCsv}
              disabled={total === 0}
              mt={20}
            >
              CSV
            </Button>
          </div>
        </Group>
      </Paper>

      {/* Ergebnisse */}
      {isLoading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : rows.length === 0 ? (
        <Group gap="xs" c="dimmed" py="xl" justify="center">
          <IconAlertCircle size={16} />
          <Text size="sm">Keine Material-Bewegungen gefunden</Text>
        </Group>
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {total} {total === 1 ? 'Eintrag' : 'Einträge'}
            {isFetching && ' · Wird aktualisiert…'}
          </Text>
          <Table striped highlightOnHover withTableBorder withColumnBorders verticalSpacing="xs" fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Datum</Table.Th>
                <Table.Th>Techniker</Table.Th>
                <Table.Th>Vorgang</Table.Th>
                <Table.Th>Artikel</Table.Th>
                <Table.Th>Artikel-Nr.</Table.Th>
                <Table.Th>Menge</Table.Th>
                <Table.Th>SNR</Table.Th>
                <Table.Th>Beleg</Table.Th>
                <Table.Th>Quelle</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row, idx) => (
                <Table.Tr key={`${row.sourceType}-${row.sourceId}-${row.serial ?? idx}`}>
                  <Table.Td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.date)}</Table.Td>
                  <Table.Td>{row.technicianName}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" variant="light" color={KIND_COLOR[row.kind]}>
                      {KIND_LABEL[row.kind]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{row.articleName}</Table.Td>
                  <Table.Td>
                    {row.articleNumber ? (
                      <Text size="xs" ff="monospace">{row.articleNumber}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>{row.quantity}</Table.Td>
                  <Table.Td>
                    {row.serial ? (
                      <Text size="xs" ff="monospace">{row.serial}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {row.ticketRef ? (
                      <Text size="xs" ff="monospace">{row.ticketRef}</Text>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{SOURCE_LABEL[row.sourceType]}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                size="sm"
              />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}

export { IconPackage };
